import { getHttpErrorMessage, getNetworkErrorMessage } from '../constants/errorMessages';

/**
 * Normaliza y extrae un mensaje de error amigable de un error de API
 * 
 * @param {Error} error - Error de axios o de la API
 * @param {string} defaultMessage - Mensaje por defecto si no se puede extraer uno
 * @returns {Object} Objeto con { message, type, statusCode }
 */
export const normalizeApiError = (error, defaultMessage = 'Ocurrió un error inesperado') => {
  // Si el error tiene una respuesta del servidor
  if (error.response) {
    const statusCode = error.response.status;
    const serverMessage = error.response?.data?.message || 
                        error.response?.data?.error ||
                        error.response?.data?.detail;
    
    // Priorizar mensaje del servidor si existe y es útil
    const message = serverMessage && serverMessage !== 'Internal Server Error'
      ? serverMessage
      : getHttpErrorMessage(statusCode);
    
    return {
      message,
      type: 'api',
      statusCode,
      originalError: error,
    };
  }
  
  // Si es un error de red o timeout
  if (error.request || error.code === 'ECONNABORTED') {
    return {
      message: getNetworkErrorMessage(error),
      type: 'network',
      statusCode: null,
      originalError: error,
    };
  }
  
  // Error desconocido
  return {
    message: error.message || defaultMessage,
    type: 'unknown',
    statusCode: null,
    originalError: error,
  };
};

/**
 * Determina si un error debe ser silenciado (no mostrar notificación)
 * Útil para errores que se manejan de forma especial (ej: 401 con redirect)
 */
export const shouldSilenceError = (error, statusCode) => {
  // Silenciar 401: lo maneja auth/sessionExpiry, que lo marca desde useApiClient, y AutoLogin,
  // que decide que ve el usuario. Antes decia "en otro lugar" y no habia tal lugar — ese era
  // el bug de #168. Con el aviso encima, un toast de 401 seria ruido duplicado.
  if (statusCode === 401) {
    return true;
  }
  
  return false;
};

/**
 * Logs estructurado de errores para debugging
 */
export const logError = (error, context = '') => {
  const normalized = normalizeApiError(error);
  
  console.error(`[Error Handler${context ? ` - ${context}` : ''}]`, {
    message: normalized.message,
    type: normalized.type,
    statusCode: normalized.statusCode,
    originalError: normalized.originalError,
    timestamp: new Date().toISOString(),
  });
  
  return normalized;
};

